// components/Notifications/NotificationBell.jsx
import React, { useEffect, useState, useRef } from 'react';
import { Bell, X, CheckCheck, Trash2, Clock } from 'lucide-react';
import { useTheme } from '../Theme/ThemeProvider';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import { authFetch } from '../../helpers/authFetch';

function NotificationBell() {
  const { colors } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const panelRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = async (pageNum = 0) => {
    try {
      setLoading(true);
      const response = await authFetch(
        `${summaryApi.getNotifications?.url || 'https://josiesphones-server.onrender.com/api/notifications/get-notifications'}?limit=10&skip=${pageNum * 10}&unreadOnly=false`,
        {
          method: 'GET',          
        }
      );
      const data = await response.json();
      
      if (data.success) {
        setNotifications(data.data);
        setUnreadCount(data.unreadCount);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unread count
  const fetchUnreadCount = async () => {
    try {
      const response = await authFetch(
        `${summaryApi.getUnreadCount?.url || 'https://josiesphones-server.onrender.com/api/notifications/unread-count'}`,
        {
          method: 'GET',
          
        }
      );
      const data = await response.json();
      if (data.success) {
        setUnreadCount(data.unreadCount);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      await authFetch(
        `${summaryApi.markAsRead?.url || 'https://josiesphones-server.onrender.com/api/notifications/mark-as-read'}/${notificationId}`,
        {
          method: 'POST',
          
        }
      );
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await authFetch(
        `${summaryApi.markAllAsRead?.url || 'https://josiesphones-server.onrender.com/api/notifications/mark-all-read'}`,
        {
          method: 'POST',
          
        }
      );
      fetchNotifications();
      fetchUnreadCount();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await authFetch(
        `${summaryApi.deleteNotification?.url || 'https://josiesphones-server.onrender.com/api/notifications/delete'}/${notificationId}`,
        {
          method: 'DELETE',
          
        }
      );
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative">
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg transition-all hover:scale-110 active:scale-95"
        style={{
          backgroundColor: isOpen ? colors.primary : colors.bgSecondary,
          color: isOpen ? 'white' : colors.primary
        }}
      >
        <Bell size={24} />
        {unreadCount > 0 && (
          <span
            className="absolute top-0 right-0 w-5 h-5 rounded-full text-xs font-bold text-white flex items-center justify-center"
            style={{ backgroundColor: '#EF4444' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-3 w-96 rounded-xl shadow-2xl z-50 animate-in fade-in slide-in-from-top-2 duration-200"
          style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`
          }}
        >
          {/* Header */}
          <div
            className="p-4 border-b flex items-center justify-between"
            style={{ borderColor: colors.border }}
          >
            <h3 className="font-bold text-lg" style={{ color: colors.text }}>
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="p-2 rounded transition-all hover:scale-110"
                  style={{
                    backgroundColor: colors.primaryLight,
                    color: colors.primary
                  }}
                  title="Mark all as read"
                >
                  <CheckCheck size={18} />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded transition-all hover:scale-110"
                style={{
                  backgroundColor: colors.bg,
                  color: colors.text
                }}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {loading ? (
              <div className="p-6 text-center" style={{ color: colors.textSecondary }}>
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-transparent mx-auto" style={{ borderTopColor: colors.primary }} />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center" style={{ color: colors.textSecondary }}>
                <Bell size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: colors.border }}>
                {notifications.map((notification) => (
                  <div
                    key={notification._id}
                    className={`p-4 transition-all hover:brightness-110 cursor-pointer group`}
                    style={{
                      backgroundColor: notification.isRead ? colors.bg : colors.primaryLight,
                      opacity: notification.isRead ? 0.7 : 1
                    }}
                    onClick={() => !notification.isRead && markAsRead(notification._id)}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: notification.color ? `${notification.color}20` : colors.primaryLight }}
                      >
                        <span style={{ color: notification.color || colors.primary }}>
                          {getNotificationIcon(notification.type)}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm" style={{ color: colors.text }}>
                          {notification.title}
                        </p>
                        <p className="text-xs mt-1 line-clamp-2" style={{ color: colors.textSecondary }}>
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-1 mt-2 text-xs" style={{ color: colors.textSecondary }}>
                          <Clock size={12} />
                          {getTimeAgo(notification.createdAt)}
                        </div>
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification._id);
                        }}
                        className="p-1 rounded opacity-0 group-hover:opacity-100 transition-all hover:scale-110"
                        style={{
                          backgroundColor: colors.bg,
                          color: colors.accent
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="p-4 border-t text-center"
            style={{ borderColor: colors.border }}
          >
            <a
              href="/notifications"
              className="text-sm font-semibold transition-all hover:scale-105"
              style={{ color: colors.primary }}
            >
              View all notifications →
            </a>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideInFromTop {
          from { transform: translateY(-10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .animate-in {
          animation: fadeIn 0.2s ease-out, slideInFromTop 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

// Helper function to get notification icon
function getNotificationIcon(type) {
  const icons = {
    product_added: '📦',
    product_reviewed: '⭐',
    product_liked: '❤️',
    profile_updated: '👤',
    login: '🔐',
    order_status: '📋'
  };
  return icons[type] || '🔔';
}

// Helper function to format time
function getTimeAgo(timestamp) {
  const now = new Date();
  const date = new Date(timestamp);
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export default NotificationBell;
